(() => {
  const config = window.sleepmartPillowPromotion;
  if (!config) return;

  const giftVariantId = Number(config.giftVariantId);
  const giftProductId = Number(config.giftProductId);
  const giftProperty = '_sleepmart_pillow_gift';
  let syncing = null;
  let queued = false;

  const endpoint = (route, suffix = '.js') => `${route || ''}${suffix}`;

  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...options,
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || config.errorMessage);
    }
    return data;
  };

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const isMattress = (item) => {
    if (Number(item.product_id) === giftProductId) return false;
    const title = normalize(item.product_title);
    const type = normalize(item.product_type);
    return title.startsWith('colchon ') || type === 'colchon' || type === 'colchones';
  };

  const isGift = (item) =>
    Number(item.variant_id) === giftVariantId &&
    String(item.properties?.[giftProperty] || '') === 'true';

  const getCart = () => request(endpoint(window.routes?.cart_url || '/cart'));

  const updateCartCounts = (cart) => {
    const itemCount = Number(
      cart.item_count ??
        cart.items.reduce((total, item) => total + Number(item.quantity || 0), 0)
    );
    const itemLabel =
      itemCount === 1
        ? config.cartCountOne
        : config.cartCountOther.replace('[count]', String(itemCount));

    document.querySelectorAll('.sm-cart-count').forEach((element) => {
      element.textContent = String(itemCount);
      element.setAttribute('aria-label', itemLabel);
    });

    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;

    const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
    const accessibleCount = bubble.querySelector('.visually-hidden');
    if (visibleCount) visibleCount.textContent = String(itemCount);
    if (accessibleCount) accessibleCount.textContent = itemLabel;
  };

  const publishCartUpdate = async (cart) => {
    updateCartCounts(cart);
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    await publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'sleepmart-pillow-promotion',
      cartData: cart,
      variantId: giftVariantId,
    });
  };

  const addGift = (quantity) =>
    request(endpoint(window.routes?.cart_add_url || '/cart/add'), {
      method: 'POST',
      body: JSON.stringify({
        items: [
          {
            id: giftVariantId,
            quantity,
            properties: {
              [giftProperty]: 'true',
            },
          },
        ],
      }),
    });

  const setGiftQuantity = (giftLines, desiredQuantity) => {
    const updates = {};
    giftLines.forEach((item, index) => {
      updates[item.key] = index === 0 ? desiredQuantity : 0;
    });

    return request(endpoint(window.routes?.cart_update_url || '/cart/update'), {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
  };

  const synchronize = async () => {
    const cart = await getCart();
    const desiredQuantity = cart.items
      .filter(isMattress)
      .reduce((total, item) => total + item.quantity, 0);
    const giftLines = cart.items.filter(isGift);
    const giftQuantity = giftLines.reduce((total, item) => total + item.quantity, 0);

    if (giftQuantity === desiredQuantity) {
      updateCartCounts(cart);
      return cart;
    }

    if (giftQuantity < desiredQuantity) {
      await addGift(desiredQuantity - giftQuantity);
    } else {
      await setGiftQuantity(giftLines, desiredQuantity);
    }

    const updatedCart = await getCart();
    await publishCartUpdate(updatedCart);
    document.dispatchEvent(
      new CustomEvent('sleepmart:pillow-promotion-updated', {
        detail: {
          cart: updatedCart,
          giftQuantity: desiredQuantity,
        },
      })
    );
    return updatedCart;
  };

  const sync = () => {
    if (syncing) {
      queued = true;
      return syncing;
    }

    syncing = synchronize()
      .catch((error) => {
        document.dispatchEvent(
          new CustomEvent('sleepmart:pillow-promotion-error', {
            detail: { error },
          })
        );
        throw error;
      })
      .finally(async () => {
        syncing = null;
        if (queued) {
          queued = false;
          await sync();
        }
      });

    return syncing;
  };

  const proceedToCheckout = async (event) => {
    const trigger = event.target.closest('[name="checkout"], a[href*="/checkout"]');
    if (!trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    trigger.setAttribute('aria-disabled', 'true');

    try {
      await sync();
      window.location.assign('/checkout');
    } catch {
      trigger.removeAttribute('aria-disabled');
      window.alert(config.errorMessage);
    }
  };

  const handleCheckoutSubmit = async (event) => {
    if (!event.target.querySelector('[name="checkout"]')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      await sync();
      window.location.assign('/checkout');
    } catch {
      window.alert(config.errorMessage);
    }
  };

  window.SleepmartPillowPromotion = { sync };

  document.addEventListener('click', proceedToCheckout, true);
  document.addEventListener('submit', handleCheckoutSubmit, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'sleepmart-pillow-promotion') sync().catch(() => {});
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync().catch(() => {}), { once: true });
  } else {
    sync().catch(() => {});
  }
})();
